import { createCookie } from '@remix-run/node';
import { TFnType } from '@tolgee/core';

export const i18nCookie = createCookie('i18n', {
  sameSite: 'lax',
  path: '/',
});

type TMessageResolver = {
  required: string;
  invalid: string;
  min?: string;
  max?: string;
  percentRange?: string;
};

type TMessageResolverOptionValue = {
  [key: string]: string | number;
};

type OptionalMessageResolver<K extends string | number | symbol = string> = Partial<
  Record<K, TMessageResolverOptionValue>
>;

const defaultValuesOption = {
  minValue: 0,
};

export const tMessageResolver = <
  FieldsBase extends Record<string, any>,
  K extends keyof FieldsBase = keyof FieldsBase,
>(
  t: TFnType,
  fields: K[],
  options?: OptionalMessageResolver<K & (string | number | symbol)>,
): Record<K, TMessageResolver> => {
  return fields.reduce(
    (acc, field) => {
      // field is a key of FieldsBase, but t expects a string key for translation
      const fieldKey = String(field);
      const tOptions = {
        field: t(fieldKey),
        ...defaultValuesOption,
        ...options?.[fieldKey as unknown as keyof typeof options],
      };
      const base: TMessageResolver = {
        required: t('validation:required_field', { ...tOptions }),
        invalid: t('validation:invalid_field', { ...tOptions }),
        min: t('validation:min_value_field', { ...tOptions }),
        max: t('validation:max_value_field', { ...tOptions }),
        percentRange: t('validation:must_be_between_0_and_100', { ...tOptions }),
      };

      const override = options?.[field as unknown as keyof typeof options];

      acc[field] = { ...(base as any), ...(override ?? {}) } as TMessageResolver;
      return acc as Record<K, TMessageResolver>;
    },
    {} as Record<K, TMessageResolver>,
  );
};
